import React from "react";

export const STATUS_KEYS = [
  "received", "out_for_delivery", "attempted", "delivered", "returned", "canceled",
];

/** Chaves i18n das etapas — derivadas de STATUS_KEYS */
export const STEP_LABEL_KEYS = STATUS_KEYS.map((s) => `status_${s}`);

export const STATUS_COLORS = {
  received: "bg-zinc-100 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-100",
  sorting: "bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300",
  out_for_delivery: "bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-300",
  attempted: "bg-yellow-50 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-300",
  delivered: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
  returned: "bg-orange-50 text-orange-700 dark:bg-orange-950 dark:text-orange-300",
  canceled: "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300",
};

export const STATUS_DOTS = {
  received: "bg-zinc-500",
  sorting: "bg-blue-500",
  out_for_delivery: "bg-orange-500",
  attempted: "bg-yellow-500",
  delivered: "bg-emerald-500",
  returned: "bg-orange-500",
  canceled: "bg-red-500",
};

export function canonicalStatus(status) {
  return status === "sorting" ? "received" : status;
}

export function StatusChip({ status, t }) {
  const key = canonicalStatus(status);
  const map = {
    delivered: "pill pill-success",
    returned: "pill pill-warning",
    canceled: "pill pill-danger",
    out_for_delivery: "pill pill-info",
    attempted: "pill pill-warning",
    received: "pill pill-neutral",
    sorting: "pill pill-info",
  };
  const cls = map[key] || "pill pill-neutral";
  return (
    <span className={cls} data-testid={`status-chip-${key}`}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${STATUS_DOTS[key]}`} />
      {t(`status_${key}`)}
    </span>
  );
}

export function fmtMoney(v) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v || 0));
}

export function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

export function docProtocol(doc) {
  return doc?.protocol_number || doc?.number || "—";
}

const PROTOCOL_DATE_DIGITS = 8;
const PROTOCOL_PREFIX_MAX = 4;

function isCompleteProtocolDate(rest) {
  if (rest.length !== PROTOCOL_DATE_DIGITS) return false;
  const day = Number(rest.slice(0, 2));
  const month = Number(rest.slice(2, 4));
  const year = Number(rest.slice(4, 8));
  return day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1990 && year <= 2100;
}

function formatProtocolParts(prefix, rest) {
  if (!rest) return prefix;
  const day = rest.slice(0, 2);
  const month = rest.slice(2, 4);
  const year = rest.slice(4, 8);
  let formatted = `${prefix}-${day}`;
  if (month) formatted += `/${month}`;
  if (year) formatted += `/${year}`;
  return formatted;
}

/** Formats protocol as {1-4 digits}-DD/MM/YYYY while typing. */
export function formatProtocolInput(raw) {
  const digits = String(raw || "").replace(/\D/g, "").slice(0, PROTOCOL_PREFIX_MAX + PROTOCOL_DATE_DIGITS);
  if (!digits) return "";

  if (digits.length >= 9) {
    const prefix = digits.slice(0, digits.length - PROTOCOL_DATE_DIGITS);
    const rest = digits.slice(-PROTOCOL_DATE_DIGITS);
    if (prefix.length >= 1 && prefix.length <= PROTOCOL_PREFIX_MAX && isCompleteProtocolDate(rest)) {
      return formatProtocolParts(prefix, rest);
    }
  }

  if (digits.length <= PROTOCOL_PREFIX_MAX) return digits;
  return formatProtocolParts(digits.slice(0, PROTOCOL_PREFIX_MAX), digits.slice(PROTOCOL_PREFIX_MAX));
}

export function formatDocumentsSearchInput(raw) {
  const value = String(raw ?? "");
  if (!value) return "";
  if (/[A-Za-zÀ-ÿ]/.test(value)) return value;
  return formatProtocolInput(value);
}

function normalizeOccurrenceText(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

export function isRefusedOccurrence(itemOrText) {
  if (itemOrText && typeof itemOrText === "object") {
    const code = String(itemOrText.code || "").trim().toUpperCase();
    if (code === "REC") return true;
    return isRefusedOccurrence(itemOrText.label || itemOrText.reason || itemOrText.note || "");
  }
  const text = normalizeOccurrenceText(itemOrText);
  return text === "rec" || text.includes("recusado");
}

export function isSuccessfulDeliveryOccurrence(item) {
  if (!item || isRefusedOccurrence(item)) return false;
  const code = String(item.code || "").trim().toUpperCase();
  if (code === "ENT_OK" || code === "ENT") return true;
  const label = normalizeOccurrenceText(item.label || item.reason || "");
  return label === "entrega realizada" || label === "entregue";
}

export function occurrenceFromCatalogItem(item) {
  if (!item) return { outcome: "attempted", reason: null, delivered: false, refused: false };
  const refused = isRefusedOccurrence(item);
  const delivered = isSuccessfulDeliveryOccurrence(item);
  return {
    outcome: delivered ? "delivered" : "attempted",
    reason: item.label || null,
    delivered,
    refused,
  };
}

export function isRefusedDocument(doc) {
  if (doc?.status !== "attempted" && doc?.status !== "returned") return false;
  const last = doc?.history?.[doc.history.length - 1];
  return isRefusedOccurrence(last?.note);
}

export function boardColumnForDocument(doc) {
  if (isRefusedDocument(doc)) return "delivered";
  if (doc?.status === "assigned") return "out_for_delivery";
  if (doc?.status === "sorting") return "received";
  return doc?.status;
}

export function displayStatusForDocument(doc) {
  return boardColumnForDocument(doc);
}

export function documentMovementHistory(doc) {
  const history = Array.isArray(doc?.history) ? doc.history.filter(Boolean).map((h) => ({ ...h })) : [];
  const hasReceived = history.some((h) => h.status === "received");
  if (!hasReceived && doc?.created_at) {
    history.unshift({
      status: "received",
      at: doc.created_at,
      note: "Documento recebido",
    });
  }
  history.sort((a, b) => {
    const ta = new Date(a.at || 0).getTime();
    const tb = new Date(b.at || 0).getTime();
    return ta - tb;
  });
  return history;
}

export function sortDocumentsByProtocol(docs) {
  return [...(docs || [])].sort((a, b) => {
    const pa = String(a?.protocol_number || a?.number || "");
    const pb = String(b?.protocol_number || b?.number || "");
    if (!pa && !pb) return 0;
    if (!pa) return 1;
    if (!pb) return -1;
    return pa.localeCompare(pb, "pt-BR", { numeric: true, sensitivity: "base" });
  });
}
